import { useEffect, useState } from "react";
import { TextAa } from "@phosphor-icons/react";

/**
 * Header text-size toggle for senior farmers.
 *
 * Multiplies the html font-size by a factor stored in localStorage:
 *   100% → 17.5px base (default from index.css)
 *   115% → 20.1px
 *   130% → 22.75px
 *
 * We set `document.documentElement.style.fontSize` — this cascades
 * through every Tailwind rem/em token.
 */
const STEPS = [
  { key: "1.00", label: "A", pct: "100%", scale: 1.0 },
  { key: "1.15", label: "A", pct: "115%", scale: 1.15 },
  { key: "1.30", label: "A", pct: "130%", scale: 1.3 },
];

const STORAGE_KEY = "fp:text-scale";
const BASE_PX_DESKTOP = 17.5;
const BASE_PX_MOBILE = 16.5;

function applyScale(scale) {
  if (typeof document === "undefined") return;
  const mobile = window.matchMedia("(max-width: 640px)").matches;
  const base = mobile ? BASE_PX_MOBILE : BASE_PX_DESKTOP;
  document.documentElement.style.fontSize = `${(base * scale).toFixed(2)}px`;
}

export default function TextSizeToggle({ compact = false }) {
  const [scale, setScale] = useState(() => {
    if (typeof window === "undefined") return 1.0;
    const stored = parseFloat(localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(stored) && stored > 0.5 ? stored : 1.0;
  });

  // Apply on mount + resize (so mobile ↔ desktop switch also updates base)
  useEffect(() => {
    applyScale(scale);
    const onResize = () => applyScale(scale);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [scale]);

  const setStep = (s) => {
    setScale(s);
    try { localStorage.setItem(STORAGE_KEY, String(s)); } catch { /* ignore */ }
  };

  return (
    <div
      className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white/80 backdrop-blur px-1.5 py-1"
      data-testid="text-size-toggle"
      role="group"
      aria-label="Adjust text size"
    >
      <TextAa size={14} weight="bold" className="ml-1 opacity-70" aria-hidden />
      {STEPS.map((step, i) => {
        const active = Math.abs(scale - step.scale) < 0.01;
        return (
          <button
            key={step.key}
            onClick={() => setStep(step.scale)}
            data-testid={`text-size-${step.pct.replace("%", "")}`}
            aria-pressed={active}
            aria-label={`Set text size to ${step.pct}`}
            title={`Text size ${step.pct}`}
            className={`rounded-full px-2 py-0.5 font-serif-display leading-none transition ${active ? "bg-[color:var(--np-brand,#B85F2A)] text-white" : "hover:bg-black/5 text-[#241209]"}`}
            style={{ fontSize: `${11 + i * 3}px` }}
          >
            {step.label}
          </button>
        );
      })}
      {!compact && (
        <span className="pr-1.5 pl-0.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#3A3830] hidden md:inline">
          Size
        </span>
      )}
    </div>
  );
}
